import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { DialogRef } from '@angular/cdk/dialog';
import { Subscription } from 'rxjs';

import { labelMinLength, userNameMaxLength } from '../../../app.constants';
import { ConfigService } from '../../../config.service';
import { CoreApiService } from '../../../core-api.service';
import { LoggingService } from '../../../logging.service';
import { NotificationService } from '../../../notification.service';
import { ProjectsService } from '../../../projects/projects.service';
import { SiteRole } from '../../../shared/enums/site-role.enum';
import { SelectOption } from '../../../shared/form.utils';
import { User } from '../../../shared/models/user.model';
import { generateElementId } from '../../../shared/string.utils';

export type CreateUserDialogResult = Pick<User, '_id'>

interface SiteRoleOption extends SelectOption {
  disabled?: boolean
}

@Component({
  host: {
    class: 'mat-dialog-component',
    'test-id': 'create-group-dialog',
  },
  selector: 'create-user-dialog',
  styleUrls: ['./create-user-dialog.component.scss'],
  templateUrl: './create-user-dialog.component.html',
})
export class CreateUserDialogComponent implements OnDestroy {
  readonly elementId = generateElementId()
  readonly form: FormGroup
  readonly siteRoleOptions: SiteRoleOption[] = [
    { value: SiteRole.User, label: 'User' },
    { value: SiteRole.Developer, label: 'Developer' },
    { value: SiteRole.SiteAdmin, label: 'Site Admin' },
  ]

  private subscriptions = {
    users: Subscription.EMPTY,
    settings: Subscription.EMPTY,
  }
  private siteUsers: User[] = []

  constructor(
    private configService: ConfigService,
    public dialogRef: DialogRef<CreateUserDialogResult, CreateUserDialogComponent>,
    private coreApi: CoreApiService,
    private logger: LoggingService,
    private notifications: NotificationService,
    private projectsService: ProjectsService,
  ) {
    this.form = new FormGroup({
      firstName: new FormControl('', [Validators.required, Validators.minLength(labelMinLength), Validators.maxLength(userNameMaxLength)]),
      lastName: new FormControl('', [Validators.required, Validators.minLength(labelMinLength), Validators.maxLength(userNameMaxLength)]),
      emailAddress: new FormControl('', [Validators.required, Validators.email, this.uniqueEmailAddress]),
      siteRole: new FormControl(SiteRole.User),
      ldap_sync_id: new FormControl(null),
    });

    this.subscriptions.settings = this.configService.getSiteSettings().subscribe(settings => {
      if (settings.ldap_sync?.group_dn) {
        const siteAdminRole = this.siteRoleOptions.find(option => option.value === SiteRole.SiteAdmin);
        siteAdminRole.disabled = true;
      }
    });
    this.subscriptions.users = this.projectsService.getUsers().subscribe(users => {
      this.siteUsers = users;
    });
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  submit(): void {
    if (this.form.invalid) {
      return;
    }
    this.coreApi.users.post({
      _id: this.form.value.emailAddress,
      firstname: this.form.value.firstName,
      lastname: this.form.value.lastName,
      email: this.form.value.emailAddress,
      ldap_sync_id: this.form.value.ldap_sync_id,
      roles: [this.form.value.siteRole],
    }).subscribe({
      next: user => {
        this.projectsService.getUsers({ refresh: true });
        this.notifications.success('userManagement.createNew.success');
        this.dialogRef.close(user);
      },
      error: (error: HttpErrorResponse) => {
        if (error.status === 409) {
          // we check for email conflicts as validation, but just in case
          this.notifications.error('userManagement.createNew.conflict');
        } else {
          this.logger.error(error);
          this.notifications.error('userManagement.createNew.error');
        }
      },
    });
  }

  private uniqueEmailAddress: ValidatorFn = control => {
    if (!control.value) {
      return null;
    }
    const existingUser = this.siteUsers.find(user => user.email === control.value);
    if (existingUser) {
      return existingUser.disabled ? { existingDisabledUser: true } : { existingActiveUser: true };
    }
    return null;
  }

  getErrorMessage(controlName: string, label: string): string {

    const control = this.form.controls[controlName];

    if (!control.touched) {
      return '';
    }

    if (control.hasError('required')) {
      return `${label} is required`
    }
    if (control.hasError('email')) {
      return 'not a valid email address'
    }
    if (control.hasError('minLength') || control.hasError('maxLength')) {
      return `${label} must be between 2 and 32 characters`
    }
    if (control.hasError('existingDisabledUser') || control.hasError('existingActiveUser')) {
      return 'A user with this email address already exists'
    }

    return '';
  }
}
